import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import InteractiveGridPattern from '../components/magicui/InteractiveGridPattern'

export default function Connect() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setShowError(false)
    setShowSuccess(false)

    try {
      // Submit to Netlify Forms
      const formElement = e.target
      const formDataToSend = new FormData(formElement)
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend).toString(),
      })

      if (!response.ok) {
        throw new Error('Form submission failed')
      }

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: '',
      })

      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 5000)
    } catch (error) {
      console.error('Error submitting form:', error)
      setShowError(true)
      setTimeout(() => setShowError(false), 5000)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Copy email to clipboard
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText('connect@helloheman.com')
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy email:', error)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      {/* Interactive Grid Pattern Background */}
      <div className="absolute inset-0 z-0">
        <InteractiveGridPattern
          className="h-full w-full"
          width={60}
          height={60}
          squares={[32, 32]}
          squaresClassName="stroke-gray-800/20 hover:fill-gray-800/10"
        />
      </div>

      {/* Animated Stars Background */}
      <div className="absolute inset-0 stars"></div>
      <div className="absolute inset-0 stars2"></div>

      {/* Main Content */}
      <div className="relative z-10 px-4 sm:px-6 py-4 sm:py-6 max-w-7xl mx-auto pt-24 sm:pt-32 md:pt-40">
        {/* Black Container for Content */}
        <div className="bg-black border border-gray-800 rounded-2xl p-8 shadow-2xl">
          {/* Page Header */}
          <div className="mb-12 text-center">
            <motion.div
              className="text-gray-400 text-xs sm:text-sm mb-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {'// connect'}
            </motion.div>
            <motion.div
              className="text-4xl md:text-5xl font-bold mb-1 text-white font-monument-extended"
              style={{
                fontFamily: "'Clash Display', 'Arial Black', sans-serif",
                fontWeight: 700,
                letterSpacing: '-0.01em',
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              get in touch
            </motion.div>
            <motion.div
              className="text-[#E06100] text-xs sm:text-sm uppercase font-semibold"
              style={{ letterSpacing: '2px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Ready to start a conversation
            </motion.div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Contact Information */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 font-title">Get In Touch</h2>
                <div className="flex space-x-4 mb-2">
                  <button
                    onClick={copyEmail}
                    className="group flex items-center px-4 py-2 bg-black border border-gray-400 rounded-full hover:bg-gray-600 hover:border-gray-400 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                      {isCopied ? (
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-gray-400 font-medium group-hover:text-white transition-colors duration-300 underline decoration-gray-400 group-hover:decoration-white font-syne">
                      {isCopied ? 'Copied!' : 'connect@helloheman.com'}
                    </span>
                  </button>
                </div>
              </div>

                  {/* Social Links */}
              <div>
                <div className="flex space-x-4 mb-2">
                  {/* Instagram Link */}
                  <a
                    href="https://instagram.com/hemanshakthi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center px-4 py-2 bg-black border border-pink-400 rounded-full hover:bg-pink-500 hover:border-pink-400 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </div>
                    <span className="text-pink-400 font-medium group-hover:text-white transition-colors duration-300 underline decoration-pink-400 group-hover:decoration-white font-syne">
                      Instagram
                    </span>
                  </a>

                  {/* LinkedIn Link */}
                  <a
                    href="https://www.linkedin.com/in/heman-shakthi/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center px-4 py-2 bg-black border border-blue-400 rounded-full hover:bg-blue-600 hover:border-blue-400 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </div>
                    <span className="text-blue-400 font-medium group-hover:text-white transition-colors duration-300 underline decoration-blue-400 group-hover:decoration-white font-syne">
                      LinkedIn
                    </span>
                  </a>
                </div>
                
                <div className="flex space-x-4 mb-2">
                  {/* Book a Call Button */}
                  <a
                    href="https://cal.com/hemanshakthi/15min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center px-4 py-2 bg-black border border-[#E06100] rounded-full hover:bg-[#E06100] hover:border-[#E06100] transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#E06100] flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 2v4" />
                        <path d="M16 2v4" />
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M3 10h18" />
                        <path d="M8 14h.01" />
                        <path d="M12 14h.01" />
                        <path d="M16 14h.01" />
                      </svg>
                    </div>
                    <span className="text-[#E06100] font-medium group-hover:text-white transition-colors duration-300 underline decoration-[#E06100] group-hover:decoration-white font-syne">
                      Book a Call
                    </span>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6 font-title">Send a Message</h2>
              <p className="text-gray-300 leading-relaxed mb-6 font-syne">
                Have a project in mind? Let&apos;s discuss how we can work together to bring your
                ideas to life.
              </p>

              <form 
                onSubmit={handleSubmit} 
                className="space-y-6 mt-6"
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
              >
                {/* Hidden fields for Netlify */}
                <input type="hidden" name="form-name" value="contact" />
                {/* Honeypot field for spam protection */}
                <div style={{ display: 'none' }}>
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-300 mb-2 font-syne"
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-all duration-200"
                      placeholder="Your first name"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-300 mb-2 font-syne"
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-all duration-200"
                      placeholder="Your last name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-300 mb-2 font-syne"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-all duration-200"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-300 mb-2 font-syne"
                  >
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-all duration-200"
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="project-inquiry">Project Inquiry</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="consultation">Consultation</option>
                    <option value="general">General Question</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-300 mb-2 font-syne"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-all duration-200 resize-none"
                    placeholder="Tell me about your project or what you'd like to discuss..."
                    required
                  ></textarea>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-white text-black font-semibold rounded-lg hover:bg-gray-200 active:bg-[#E06100] active:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                  <p className="text-gray-400 text-sm font-syne">
                    I&apos;ll get back to you within 24 hours
                  </p>
                </div>
              </form>

              {/* Success/Error Messages */}
              {showSuccess && (
                <motion.div
                  className="p-4 bg-green-900 border border-green-700 rounded-lg mt-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-green-300">Message sent successfully! I'll get back to you within 24 hours.</p>
                </motion.div>
              )}

              {showError && (
                <motion.div
                  className="p-4 bg-red-900 border border-red-700 rounded-lg mt-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-red-300">There was an error sending your message. Please try again.</p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}